import { isB2B, isUkPlatform } from '@/core/settings/utils';
import { articlePathToSlugId } from '@/domains/productDiscovery/ProductPage/utils/productUrl/articlePathToSlugId';
import { PromoTags } from '@/productDiscovery/CommercialAnimation/interfaces/tags';
import { ProductCardData } from '@/productDiscovery/productCards/interfaces/productCardData';
import { SponsoredProductsLocationType } from '@/productDiscovery/SponsoredProducts/interfaces/SponsoredProducts';

import { AlgoliaHit, SponsoredData } from '../../interfaces/algoliaResponse';

export const mapAlgoliaHitToProductCardData = (
  hits: (AlgoliaHit & SponsoredData)[],
): ProductCardData[] =>
  hits.map((hit) => ({
    articleId: String(hit.article_id),
    modelId: String(hit.model_id),
    productId: hit.objectID,
    variantsCount: hit.models_count,
    title: hit.title,
    slug: articlePathToSlugId(`${hit.url}-${hit.article_id}`).slug,
    images: { largeUrl: hit.image_fullpath },
    brand: {
      id: String(hit.brand_id),
      name: hit.brand_name,
      logo: hit.brand_image_fullpath,
    },
    seller: {
      id: String(hit.seller_id),
      isMF: hit.is_mmf,
      name: hit.seller_name,
    },
    price: generatePrices(hit),
    commercialAnimation: {
      tags: (hit.commercial_animation?.tags || []) as PromoTags[],
      endAt: hit.commercial_animation?.promotion_end_at || null,
    },
    hasSponsoredLabel: !!hit.isSponsored,
    ...(hit.isSponsored && {
      sponsoredData: {
        adTrackingData: hit.adTrackingData || '',
        cpc: hit.cpc || 0,
        adPayer: hit.adPayer,
        location: SponsoredProductsLocationType.SEARCH_BAR,
        position: hit.position || null,
      },
    }),
    categoriesLastId: hit['categories.last_id'],
    categoriesLast: hit['categories.last'],
    hasPromo: !!(
      hit.commercial_animation?.tags &&
      hit.commercial_animation.tags.length > 0 &&
      hit.prices.discount
    ),
    isPro: !!hit.is_b2b,
    isCompetitive: !!hit.is_competitive,
  }));

export function getTaxKey() {
  return isB2B() && !isUkPlatform() ? 'without_vat' : 'with_vat';
}

function generatePrices(hit: AlgoliaHit): ProductCardData['price'] {
  const { per_item: item } = hit.prices;
  const currency = isUkPlatform() ? 'GBP' : 'EUR';
  const currentPriceWithVat = item.actual_price.with_vat.as_float;
  const currentPriceWithoutVat = item.actual_price.without_vat.as_float;
  const retailPriceWithVat = item.retail_price?.with_vat.as_float;
  const retailPriceWithoutVat = item.retail_price?.without_vat.as_float;
  const minQuantity = hit.min_quantity || 1;

  return {
    currentPrice: {
      amountWithVat: currentPriceWithVat * minQuantity,
      amountWithoutVat: currentPriceWithoutVat * minQuantity,
      currency,
    },
    retailPrice: {
      amountWithVat:
        retailPriceWithVat && retailPriceWithVat !== currentPriceWithVat
          ? retailPriceWithVat * minQuantity
          : undefined,
      amountWithoutVat:
        retailPriceWithoutVat && retailPriceWithoutVat !== currentPriceWithVat
          ? retailPriceWithoutVat * minQuantity
          : undefined,
      currency,
    },
  };
}
